import type { LucideIcon } from "lucide-react";
import { TrendingDown, TrendingUp } from "lucide-react";

import { cn } from "@/lib/utils";

interface StatCardProps {
  label: string;
  value: string | number;
  hint?: string;
  trend?: number; // percentage; positive=up, negative=down
  icon?: LucideIcon;
  iconColor?: "primary" | "success" | "warning" | "danger" | "info" | "purple";
  className?: string;
}

const iconColorMap = {
  primary: "bg-primary-50 text-primary-600 dark:bg-primary-500/10 dark:text-primary-300",
  success: "bg-emerald-50 text-emerald-600 dark:bg-emerald-500/10 dark:text-emerald-300",
  warning: "bg-amber-50 text-amber-600 dark:bg-amber-500/10 dark:text-amber-300",
  danger: "bg-rose-50 text-rose-600 dark:bg-rose-500/10 dark:text-rose-300",
  info: "bg-sky-50 text-sky-600 dark:bg-sky-500/10 dark:text-sky-300",
  purple: "bg-violet-50 text-violet-600 dark:bg-violet-500/10 dark:text-violet-300",
};

export function StatCard({
  label,
  value,
  hint,
  trend,
  icon: Icon,
  iconColor = "primary",
  className,
}: StatCardProps) {
  return (
    <div
      className={cn(
        "group relative rounded-2xl border border-slate-200/60 dark:border-white/10",
        "bg-white/70 dark:bg-card/40 backdrop-blur",
        "p-5 transition-all duration-300",
        "hover:-translate-y-0.5 hover:shadow-[0_4px_8px_rgba(15,23,42,0.04),0_24px_48px_-16px_rgba(15,23,42,0.16)]",
        "hover:border-slate-300/60 dark:hover:border-white/15",
        className,
      )}
    >
      <div className="flex items-start justify-between">
        <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
          {label}
        </p>
        {Icon && (
          <div className={cn("flex h-9 w-9 items-center justify-center rounded-xl", iconColorMap[iconColor])}>
            <Icon className="h-4 w-4" />
          </div>
        )}
      </div>
      <div className="mt-3 flex items-baseline gap-2">
        <span className="font-sans text-3xl font-bold tracking-tight tabular-nums">
          {value}
        </span>
        {typeof trend === "number" && (
          <span
            className={cn(
              "inline-flex items-center gap-0.5 text-xs font-semibold",
              trend > 0 ? "text-emerald-600 dark:text-emerald-400" : trend < 0 ? "text-rose-600 dark:text-rose-400" : "text-muted-foreground",
            )}
          >
            {trend > 0 ? <TrendingUp className="h-3 w-3" /> : trend < 0 ? <TrendingDown className="h-3 w-3" /> : null}
            {Math.abs(trend).toFixed(1)}%
          </span>
        )}
      </div>
      {hint && <p className="mt-1 text-xs text-muted-foreground">{hint}</p>}
    </div>
  );
}
